import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { AlertTriangle, Archive } from 'lucide-react';
import type { SpendLeakageReport } from '../../lib/api';
import { categoryOptions } from './leakageFormat';

export interface NewLeakageItem {
  po_number: string;
  description: string;
  category: string;
  spend_amount: string;
  contracted_price: string;
  transaction_date: string;
  notes: string;
}

export const EMPTY_NEW_ITEM: NewLeakageItem = {
  po_number: '',
  description: '',
  category: '',
  spend_amount: '',
  contracted_price: '',
  transaction_date: '',
  notes: '',
};

interface AddLeakageItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: NewLeakageItem;
  onChange: (value: NewLeakageItem) => void;
  onSubmit: () => void;
}

export function AddLeakageItemDialog({ open, onOpenChange, value, onChange, onSubmit }: AddLeakageItemDialogProps) {
  const set = (patch: Partial<NewLeakageItem>) => onChange({ ...value, ...patch });
  const incomplete = !value.description || !value.category || !value.spend_amount || !value.transaction_date;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Leakage Item</DialogTitle>
          <DialogDescription>Add a new spend leakage item to this report</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>PO Number</Label>
            <Input value={value.po_number} onChange={(e) => set({ po_number: e.target.value })} placeholder="e.g., PO-12345" />
          </div>
          <div>
            <Label>Description *</Label>
            <Input value={value.description} onChange={(e) => set({ description: e.target.value })} placeholder="Describe the spending" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={value.category} onValueChange={(category) => set({ category })}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Spend Amount *</Label>
              <Input type="number" step="0.01" value={value.spend_amount} onChange={(e) => set({ spend_amount: e.target.value })} placeholder="0.00" />
            </div>
            <div>
              <Label>Contracted Price</Label>
              <Input type="number" step="0.01" value={value.contracted_price} onChange={(e) => set({ contracted_price: e.target.value })} placeholder="0.00" />
            </div>
          </div>
          <div>
            <Label>Transaction Date *</Label>
            <Input type="date" value={value.transaction_date} onChange={(e) => set({ transaction_date: e.target.value })} />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={value.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Additional notes..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={incomplete}>Add Item</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ReportActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: SpendLeakageReport;
  busy: boolean;
  onConfirm: () => void;
  formatCurrency: (amount: number | string) => string;
}

function ReportFigures({ report, formatCurrency }: Pick<ReportActionDialogProps, 'report' | 'formatCurrency'>) {
  return (
    <>
      <p className="font-medium">{report.title}</p>
      <p className="text-sm text-gray-500">Report #{report.report_number}</p>
      <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
        <div>
          <p className="text-gray-500">Total Spend</p>
          <p className="font-medium">{formatCurrency(report.total_spend)}</p>
        </div>
        <div>
          <p className="text-gray-500">Leakage Amount</p>
          <p className="font-medium text-red-600">{formatCurrency(report.leakage_spend)}</p>
        </div>
      </div>
    </>
  );
}

export function PublishReportDialog({ open, onOpenChange, report, busy, onConfirm, formatCurrency }: ReportActionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish Report</DialogTitle>
          <DialogDescription>
            Are you sure you want to publish this report? Once published, the report cannot be reverted to draft status.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-2">
          <ReportFigures report={report} formatCurrency={formatCurrency} />
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">This action is irreversible</p>
                <p>Publishing will finalize the report and make it available to all stakeholders.</p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy}>{busy ? 'Publishing...' : 'Confirm Publish'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ArchiveReportDialog({ open, onOpenChange, report, busy, onConfirm, formatCurrency }: ReportActionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive Report</DialogTitle>
          <DialogDescription>
            Are you sure you want to archive this report? Archived reports are hidden from the active view but remain accessible via the archive filter.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-2">
          <ReportFigures report={report} formatCurrency={formatCurrency} />
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start gap-2">
              <Archive className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Report will be archived</p>
                <p>The report will be removed from the active view but can still be accessed using the archive filter.</p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy}>{busy ? 'Archiving...' : 'Confirm Archive'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
